import { useCallback, useEffect, useId, useRef, useState } from "react";
import QRCode from "qrcode";
import {
  createNotificationAccount,
  createNotificationSubscription,
  fetchNotificationAccount,
  fetchNotificationChannels,
  fetchNotificationSubscriptions,
  revokeNotificationSubscription,
  sendNotificationTest,
} from "../api/client";
import {
  canConnectNotification,
  isSafeNotificationUri,
  normalizeNotificationAccountStatus,
  notificationSubscriptionUri,
} from "../notificationLifecycle";

const defaultNotificationApi = {
  createNotificationAccount,
  createNotificationSubscription,
  fetchNotificationAccount,
  fetchNotificationChannels,
  fetchNotificationSubscriptions,
  revokeNotificationSubscription,
  sendNotificationTest,
};

function label(value, empty = "—") {
  if (value === null || value === undefined || value === "") return empty;
  if (typeof value === "object") return empty;
  return String(value).replaceAll("_", " ");
}

export default function NotificationSettings({ user, notificationApi = defaultNotificationApi }) {
  const phoneSetupId = useId();
  const [phoneSetupExpanded, setPhoneSetupExpanded] = useState(false);
  const [notificationChannels, setNotificationChannels] = useState([]);
  const [notificationSubscriptions, setNotificationSubscriptions] = useState([]);
  const [notificationPublicUrl, setNotificationPublicUrl] = useState("");
  const [notificationAccount, setNotificationAccount] = useState({ account: null, status: "not_configured" });
  const [notificationsBusy, setNotificationsBusy] = useState(false);
  const [notificationsStale, setNotificationsStale] = useState(false);
  const [notificationRefreshError, setNotificationRefreshError] = useState("");
  const [notificationsError, setNotificationsError] = useState("");
  const [accountForm, setAccountForm] = useState({ password: "", confirm_password: "" });
  const [accountBusy, setAccountBusy] = useState(false);
  const [accountError, setAccountError] = useState("");
  const [accountSuccess, setAccountSuccess] = useState("");
  const [selectedChannelId, setSelectedChannelId] = useState("");
  const [notificationInviteUris, setNotificationInviteUris] = useState({});
  const [notificationQrData, setNotificationQrData] = useState({});
  const [revealedSubscriptions, setRevealedSubscriptions] = useState({});
  const [notificationActionBusy, setNotificationActionBusy] = useState({});
  const [pendingRevokeId, setPendingRevokeId] = useState("");
  const [focusReturnId, setFocusReturnId] = useState("");
  const revokeConfirmButton = useRef(null);
  const revokeButtons = useRef({});
  const [testNotificationBusy, setTestNotificationBusy] = useState(false);
  const [testNotificationMessage, setTestNotificationMessage] = useState("");
  const [testNotificationError, setTestNotificationError] = useState("");

  const refreshNotifications = useCallback(async () => {
    if (!user) {
      setNotificationChannels([]);
      setNotificationSubscriptions([]);
      setNotificationAccount({ account: null, status: "not_configured" });
      setNotificationPublicUrl("");
      setNotificationsStale(false);
      setNotificationRefreshError("");
      setNotificationInviteUris({});
      setNotificationQrData({});
      setRevealedSubscriptions({});
      return;
    }

    setNotificationsBusy(true);
    setNotificationsError("");
    setNotificationInviteUris({});
    setNotificationQrData({});
    setRevealedSubscriptions({});

    try {
      const [channelPayload, subscriptionsPayload, accountPayload] = await Promise.all([
        notificationApi.fetchNotificationChannels(),
        notificationApi.fetchNotificationSubscriptions(),
        notificationApi.fetchNotificationAccount(),
      ]);

      if (!Array.isArray(channelPayload.channels) || !Array.isArray(subscriptionsPayload.subscriptions)) {
        throw new Error("Notification refresh returned an invalid response.");
      }

      let publicUrl = null;
      if (channelPayload.public_server_url) {
        publicUrl = new URL(channelPayload.public_server_url);
        if (publicUrl.protocol !== "https:" || publicUrl.username || publicUrl.password || publicUrl.search || publicUrl.hash || publicUrl.pathname !== "/") {
          throw new Error("Canonical ntfy public URL is invalid.");
        }
      }

      setNotificationChannels(channelPayload.channels);
      setNotificationSubscriptions(subscriptionsPayload.subscriptions);
      setNotificationPublicUrl(publicUrl?.origin || "");

      const accountStatus = normalizeNotificationAccountStatus(accountPayload?.status);
      setNotificationAccount({
        ...accountPayload,
        test_topic: channelPayload.test_topic,
        status: accountStatus,
      });
      setAccountForm({ password: "", confirm_password: "" });

      setNotificationsStale(false);
      setNotificationRefreshError("");

      const studioUserId = String(user?.id || user?.username || "");
      const nextInviteUris = Object.fromEntries((subscriptionsPayload.subscriptions || [])
        .filter((item) => publicUrl && String(item.user_id || "") === studioUserId)
        .map((item) => [String(item.channel || ""), notificationSubscriptionUri(item, publicUrl.origin)])
        .filter(([channelId, uri]) => channelId && uri));
      if (publicUrl && accountStatus === "READY"
          && isSafeNotificationUri(channelPayload.test_subscription_uri, publicUrl.origin)) {
        nextInviteUris.__test__ = channelPayload.test_subscription_uri;
      }

      setNotificationInviteUris(accountStatus === "READY" ? nextInviteUris : {});
      setRevealedSubscriptions({});
      setTestNotificationMessage("");
      setTestNotificationError("");
      if (accountStatus !== "READY") {
        setNotificationQrData({});
      }

      setSelectedChannelId((current) => {
        const candidates = (channelPayload.channels || []).filter((channel) => channel.eligible);
        if (current && candidates.some((channel) => channel.id === current)) return current;
        return candidates[0]?.id || "";
      });
    } catch (e) {
      setNotificationsStale(true);
      setNotificationRefreshError(e.message || "Could not refresh notification status.");
      setNotificationInviteUris({});
      setNotificationQrData({});
      setRevealedSubscriptions({});
    } finally {
      setNotificationsBusy(false);
    }
  }, [notificationApi, user]);

  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications]);

  useEffect(() => {
    let active = true;

    async function buildQrCodes() {
      const entries = await Promise.all(Object.entries(notificationInviteUris)
        .filter(([channelId]) => revealedSubscriptions[channelId])
        .map(async ([channelId, uri]) => {
        try {
          const dataUrl = await QRCode.toDataURL(uri, { errorCorrectionLevel: "H", margin: 4, width: 240, color: { dark: "#000000", light: "#ffffff" } });
          return [channelId, dataUrl];
        } catch {
          return [channelId, "QR_ERROR"];
        }
        }));

      if (!active) return;
      const next = Object.fromEntries(entries.filter(([, value]) => Boolean(value)));
      setNotificationQrData(next);
      if (Object.values(next).includes("QR_ERROR")) {
        setNotificationsError("QR generation failed. Use the secure manual link below.");
      }
    }

    buildQrCodes();
    return () => { active = false; };
  }, [notificationInviteUris, revealedSubscriptions]);

  useEffect(() => {
    if (pendingRevokeId) {
      revokeConfirmButton.current?.focus();
    } else if (focusReturnId) {
      revokeButtons.current[focusReturnId]?.focus();
      setFocusReturnId("");
    }
  }, [focusReturnId, pendingRevokeId]);

  const handleAccountSetup = async (event) => {
    event.preventDefault();
    if (accountForm.password !== accountForm.confirm_password) {
      setAccountError("ntfy service password confirmation does not match.");
      return;
    }
    setAccountBusy(true);
    setAccountError("");
    setAccountSuccess("");

    try {
      const result = await notificationApi.createNotificationAccount({
        password: accountForm.password,
        confirm_password: accountForm.confirm_password,
      });

      if (String(result?.status || "").toUpperCase() !== "READY"
        || String(result?.provider_observed_state || "").toUpperCase() !== "VERIFIED") {
        throw new Error("Provider identity is not verified and durable yet.");
      }

      setAccountSuccess("ntfy account provisioned and provider-verified.");
      await refreshNotifications();
    } catch (e) {
      setAccountError(e.message || "Could not set up the notification account.");
      await refreshNotifications();
    } finally {
      setAccountForm((current) => ({ ...current, password: "", confirm_password: "" }));
      setAccountBusy(false);
    }
  };

  const handleNotificationConnect = async (channelId) => {
    setNotificationActionBusy((current) => ({ ...current, [channelId]: true }));
    setNotificationsError("");

    try {
      const result = await notificationApi.createNotificationSubscription(channelId);
      if (String(result?.status || "").toUpperCase() === "ACTIVE"
        && String(result?.subscription?.lifecycle_state || "").toUpperCase() === "ACTIVE"
        && isSafeNotificationUri(result?.uri, notificationPublicUrl)) {
        setNotificationInviteUris((current) => ({ ...current, [channelId]: result.uri }));
      } else {
        throw new Error("The channel is not provider-verified ACTIVE.");
      }
      await refreshNotifications();
    } catch (e) {
      setNotificationsError(e.message);
      await refreshNotifications();
    } finally {
      setNotificationActionBusy((current) => ({ ...current, [channelId]: false }));
    }
  };

  const handleNotificationRevoke = async (subscriptionId, channelId) => {
    setNotificationActionBusy((current) => ({ ...current, [channelId]: true }));
    setNotificationsError("");

    try {
      await notificationApi.revokeNotificationSubscription(subscriptionId);
      setNotificationInviteUris((current) => {
        const next = { ...current };
        delete next[channelId];
        return next;
      });
      setPendingRevokeId("");
      await refreshNotifications();
    } catch (e) {
      setNotificationsError(e.message);
    } finally {
      setNotificationActionBusy((current) => ({ ...current, [channelId]: false }));
    }
  };

  const toggleSubscriptionReveal = (channelId) => {
    const willReveal = !revealedSubscriptions[channelId];
    setRevealedSubscriptions(willReveal ? { [channelId]: true } : {});
    setNotificationQrData({});
  };

  const handleSendTestNotification = async () => {
    setTestNotificationBusy(true);
    setTestNotificationError("");
    setTestNotificationMessage("");
    try {
      const result = await notificationApi.sendNotificationTest();
      if (String(result?.status || "").toUpperCase() !== "SENT_TO_NTFY") {
        throw new Error("ntfy did not confirm accepting the test notification.");
      }
      setTestNotificationMessage("Sent to ntfy; handset receipt is not confirmed.");
    } catch (error) {
      setTestNotificationError(error.message || "Could not send the ntfy test notification.");
    } finally {
      setTestNotificationBusy(false);
    }
  };

  const channelOptions = notificationChannels.filter((channel) => channel.eligible);
  const accountRoleEligible = channelOptions.length > 0;
  const centralIdentityAvailable = notificationAccount?.central_identity_available === true;
  const providerIdentityRef = notificationAccount?.provider_identity_ref
    || notificationAccount?.account?.provider_identity_ref
    || notificationAccount?.account?.username
    || "";
  const expectedProviderIdentityRef = notificationAccount?.expected_provider_identity_ref || providerIdentityRef;
  const testNotificationUri = !notificationsStale && notificationInviteUris.__test__ || "";
  const testNotificationQr = notificationQrData.__test__;
  const eligibleChannel = channelOptions.find((channel) => channel.id === selectedChannelId) || null;
  const canConfigureNotifications = Boolean(user) && Boolean(notificationPublicUrl) && canConnectNotification({
    accountStatus: notificationAccount?.status,
    channelEligible: !!eligibleChannel,
    stale: notificationsStale,
  });

  return (
    <section className="studio-panel studio-notifications-panel">
      <div className="studio-panel-heading">
        <div>
          <div className="studio-section-kicker">NOTIFICATIONS</div>
          <h2 className="studio-notifications-title">Studio alerts and updates</h2>
        </div>
      </div>
      <p className="studio-muted">Your authenticated HACaMS login username is also your ntfy username. It is read-only and is never changed or transformed by this page.</p>

      <div className="studio-notification-identity-layout">
        <div className="studio-notification-identity">
          {notificationAccount?.status === "IDENTITY_CONFLICT" && (
            <div className="studio-action-error" role="alert">
              {notificationAccount.identity_error
                || `The existing ntfy username ${providerIdentityRef} does not match your HACaMS login ${expectedProviderIdentityRef}. The link was not changed; ask an administrator to review it.`}
            </div>
          )}

          {notificationAccount?.status === "READY" ? (
            <div className="studio-notification-account-form" role="status">
              <div className="studio-section-kicker">LINKED NTFY SERVICE ACCOUNT</div>
              <label className="studio-schedule-field">
                <span>HACaMS login / ntfy username</span>
                <input type="text" value={expectedProviderIdentityRef} readOnly aria-label="HACaMS login / ntfy username" />
              </label>
              <p className="studio-notification-status">
                Provisioning: {label(notificationAccount.provisioning_status || notificationAccount.status)}
                {" · "}ntfy observed: {label(notificationAccount.provider_observed_state || "VERIFIED")}
              </p>
              <p className="studio-muted">
                Use your HACaMS login username, <strong>{expectedProviderIdentityRef}</strong>, as your ntfy username.
                {" "}Use a separate ntfy service password; it is separate from your HACaMS login password,
                and HACaMS does not store or display it.
              </p>
            </div>
          ) : notificationAccount?.status === "IDENTITY_CONFLICT" ? (
            <div className="studio-notification-state" role="status">
              Account setup and test actions are disabled until an administrator reviews the existing identity link.
            </div>
          ) : centralIdentityAvailable && accountRoleEligible && !notificationsStale ? (
            <form className="studio-notification-account-form" onSubmit={handleAccountSetup}>
              <div className="studio-section-kicker">NTFY SERVICE ACCOUNT SETUP</div>
              <label className="studio-schedule-field">
                <span>HACaMS login / ntfy username</span>
                <input type="text" value={expectedProviderIdentityRef} readOnly aria-label="HACaMS login / ntfy username" />
              </label>
              <p className="studio-muted">Choose a separate ntfy service password. It is not your HACaMS login password.</p>
              {notificationAccount?.status === "FAILED_RETRYABLE" && (
                <p className="studio-muted">Retry uses the existing ntfy service password to verify or complete setup without replacing an existing credential.</p>
              )}
              {notificationAccount?.requested_state === "ACTIVE" && (
                <div className="studio-notification-state" role="status">
                  Provisioning: {label(notificationAccount.provisioning_status || notificationAccount.status)}
                  {" · "}ntfy observed: {label(notificationAccount.provider_observed_state || "UNKNOWN")}
                </div>
              )}
              <div className="studio-schedule-form-grid">
                <label className="studio-schedule-field">
                  <span>ntfy service password</span>
                  <input
                    type="password"
                    value={accountForm.password}
                    onChange={(e) => setAccountForm((current) => ({ ...current, password: e.target.value }))}
                    placeholder="At least 12 characters"
                    disabled={accountBusy}
                    autoComplete="new-password"
                  />
                </label>
                <label className="studio-schedule-field">
                  <span>Confirm ntfy service password</span>
                  <input
                    type="password"
                    value={accountForm.confirm_password}
                    onChange={(e) => setAccountForm((current) => ({ ...current, confirm_password: e.target.value }))}
                    placeholder="Repeat password"
                    disabled={accountBusy}
                    autoComplete="new-password"
                  />
                </label>
              </div>
              {accountError && <div className="studio-action-error" role="alert">{accountError}</div>}
              {accountSuccess && <div className="studio-schedule-success">{accountSuccess}</div>}
              <button className="studio-btn-primary" type="submit" disabled={accountBusy}>
                {accountBusy ? "Setting up…" : notificationAccount?.status === "FAILED_RETRYABLE" ? "Retry ntfy account setup" : "Set up ntfy service account"}
              </button>
            </form>
          ) : !centralIdentityAvailable ? (
            <div className="studio-notification-state" role="status">
              {notificationAccount?.identity_error || "A central HACaMS account identity is required before an ntfy service account can be set up."}
            </div>
          ) : !notificationsStale && !accountRoleEligible ? (
            <div className="studio-notification-state" role="status">
              Your current HACaMS role is not eligible to provision an ntfy account.
            </div>
          ) : (
            <div className="studio-notification-state" role="status">
              Notification eligibility could not be confirmed. Refresh status before setting up an ntfy service account.
            </div>
          )}
        </div>

        <div className="studio-notification-help">
          <button
            className="studio-notification-help-toggle"
            type="button"
            aria-expanded={phoneSetupExpanded}
            aria-controls={phoneSetupId}
            onClick={() => setPhoneSetupExpanded((expanded) => !expanded)}
          >
            <span className="studio-notification-help-caret" aria-hidden="true" />
            <span>HOW TO SET UP NTFY ON YOUR PHONE</span>
          </button>
          <div id={phoneSetupId} hidden={!phoneSetupExpanded}>
            <ol>
              <li>Install the official ntfy app from the Android or iOS store below.</li>
              <li>
                {notificationPublicUrl ? (
                  <>Set the custom server to <strong>{notificationPublicUrl}</strong> instead of ntfy.sh.</>
                ) : (
                  <>The ntfy server URL is not available. Ask your HACaMS administrator to configure the canonical public HTTPS ntfy URL before subscribing.</>
                )}
              </li>
              <li>
                In the ntfy app&apos;s account settings for this server, sign in with your HACaMS login username
                {" "}<strong>{expectedProviderIdentityRef}</strong> and separate ntfy service password.
              </li>
              <li>
                Channel access is restricted according to your HACaMS role. Save the account before subscribing,
                then select an eligible channel below.
              </li>
            </ol>
            <div className="studio-notification-links">
              <a href="https://play.google.com/store/apps/details?id=io.heckel.ntfy" target="_blank" rel="noreferrer">Android</a>
              <a href="https://apps.apple.com/us/app/ntfy/id1625396347" target="_blank" rel="noreferrer">iOS</a>
              <a href="https://docs.ntfy.sh/subscribe/phone/" target="_blank" rel="noreferrer">Official docs</a>
            </div>
          </div>
        </div>
      </div>

      {notificationsBusy && <div className="studio-notification-state">Loading channels…</div>}
      {notificationsStale && <div className="studio-notification-state" role="status">Showing last known notification state; refresh failed: {notificationRefreshError}</div>}
      {!notificationsStale && notificationRefreshError && <div className="studio-action-error" role="alert">{notificationRefreshError}</div>}
      {notificationsError && <div className="studio-action-error">{notificationsError}</div>}

      <div className="studio-notification-tools">
        {notificationAccount?.status === "READY" && testNotificationUri && (
          <div className="studio-notification-test" aria-label="ntfy test notification">
            <div className="studio-section-kicker">TEST NOTIFICATION</div>
            <p className="studio-muted">
              Subscribe to the read-only <strong>{notificationAccount.test_topic || "configured test topic"}</strong> topic. ntfy acceptance does not confirm handset receipt.
            </p>
            <div className="studio-notification-test-controls">
              <div className="studio-notification-secret">
                <button
                  className="studio-btn-secondary"
                  type="button"
                  onClick={() => toggleSubscriptionReveal("__test__")}
                  aria-expanded={Boolean(revealedSubscriptions.__test__)}
                >
                  {revealedSubscriptions.__test__ ? "Hide test subscription" : "Reveal test subscription"}
                </button>
                {revealedSubscriptions.__test__ && (
                  <div className="studio-notification-qr-wrap">
                    {testNotificationQr && testNotificationQr !== "QR_ERROR" && (
                      <img className="studio-notification-qr" src={testNotificationQr} alt="ntfy test-topic QR code" />
                    )}
                    <a href={testNotificationUri} target="_blank" rel="noreferrer" aria-label="Open test-topic subscription link">
                      Open test-topic subscription link
                    </a>
                  </div>
                )}
              </div>
              <div className="studio-notification-test-action">
                <button
                  className="studio-btn-secondary"
                  type="button"
                  onClick={handleSendTestNotification}
                  disabled={testNotificationBusy || notificationsStale || !accountRoleEligible}
                >
                  {testNotificationBusy ? "Sending…" : "Send test notification"}
                </button>
                {testNotificationMessage && <div className="studio-schedule-success" role="status">{testNotificationMessage}</div>}
                {testNotificationError && <div className="studio-action-error" role="alert">{testNotificationError}</div>}
              </div>
            </div>
          </div>
        )}

        <div className="studio-notification-selector-wrap">
          <label className="studio-schedule-field">
            <span>Eligible channel</span>
            <select
              value={selectedChannelId}
              onChange={(e) => {
                setSelectedChannelId(e.target.value);
                setNotificationInviteUris((current) => ({ __test__: current.__test__ }));
                setNotificationQrData((current) => ({ __test__: current.__test__ }));
                setRevealedSubscriptions((current) => ({ __test__: current.__test__ }));
                setPendingRevokeId("");
              }}
              disabled={notificationsStale || !notificationPublicUrl || notificationAccount?.status !== "READY" || !channelOptions.length}
            >
              <option value="">Select a channel</option>
              {channelOptions.map((channel) => (
                <option key={channel.id} value={channel.id}>{channel.display_name || label(channel.id, "Channel")}</option>
              ))}
            </select>
          </label>
          {!notificationPublicUrl ? (
            <div className="studio-muted">Channel subscriptions are unavailable until the canonical public ntfy URL is configured.</div>
          ) : (notificationAccount?.status !== "READY" || !selectedChannelId) && (
            <div className="studio-muted">Complete the ntfy account setup before enabling channel subscriptions.</div>
          )}
        </div>
      </div>

      {selectedChannelId && (() => {
        const channel = notificationChannels.find((entry) => entry.id === selectedChannelId);
        const record = notificationSubscriptions.find((item) => {
          const sameChannel = String(item.channel) === String(channel?.id || "");
          const sameUser = String(item.user_id) === String(user?.id || user?.username || "");
          return sameChannel && sameUser && String(item.lifecycle_state || "").toUpperCase() !== "REVOKED";
        });

        const statusLabel = record
          ? String(record.lifecycle_state || "").toUpperCase() === "ACTIVE"
            ? "Provider access active"
            : String(record.lifecycle_state || "").toUpperCase() === "REVOKED"
              ? "Subscription revoked"
              : "Subscription pending"
          : "Subscription not set up";

        const inviteUri = notificationInviteUris[selectedChannelId];
        const qrData = notificationQrData[selectedChannelId];

        return (
          <div className="studio-notification-item">
            <div className="studio-notification-copy">
              <div className="studio-notification-name">{channel?.display_name || label(channel?.id, "Channel")}</div>
              <div className="studio-notification-status">{statusLabel}</div>
              {record && String(record.lifecycle_state || "").toUpperCase() === "ACTIVE" && (
                <p className="studio-muted">Provider state only; this does not confirm app connectivity or handset receipt.</p>
              )}
            </div>
            <div className="studio-notification-actions">
              {canConfigureNotifications && (
                <>
                  {!record ? (
                    <button
                      className="studio-btn-primary"
                      type="button"
                      onClick={() => handleNotificationConnect(selectedChannelId)}
                      disabled={notificationActionBusy[selectedChannelId] || !canConfigureNotifications}
                    >
                      {notificationActionBusy[selectedChannelId] ? "Connecting…" : "Connect channel"}
                    </button>
                  ) : (
                    <>
                      <button
                        className="studio-btn-secondary"
                        type="button"
                        ref={(element) => {
                          if (element) revokeButtons.current[record.id] = element;
                          else delete revokeButtons.current[record.id];
                        }}
                        onClick={() => {
                          setFocusReturnId(record.id);
                          setPendingRevokeId(record.id);
                        }}
                        disabled={notificationActionBusy[selectedChannelId] || !canConfigureNotifications || pendingRevokeId === record.id}
                      >
                        Revoke
                      </button>
                      {pendingRevokeId === record.id && (
                        <div className="studio-notification-revoke-confirm" role="group" aria-label={`Confirm revoke for ${channel?.display_name || label(channel?.id, "channel")}`}>
                          <span>Revoke provider access for this channel?</span>
                          <button
                            className="studio-btn-danger"
                            type="button"
                            ref={revokeConfirmButton}
                            onClick={() => handleNotificationRevoke(record.id, selectedChannelId)}
                            disabled={notificationActionBusy[selectedChannelId]}
                          >
                            {notificationActionBusy[selectedChannelId] ? "Revoking…" : "Confirm revoke"}
                          </button>
                          <button
                            className="studio-btn-secondary"
                            type="button"
                            onClick={() => {
                              setPendingRevokeId("");
                              setFocusReturnId(record.id);
                            }}
                            disabled={notificationActionBusy[selectedChannelId]}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {inviteUri && (
                <div className="studio-notification-secret">
                  <button
                    className="studio-btn-secondary"
                    type="button"
                    onClick={() => toggleSubscriptionReveal(selectedChannelId)}
                    aria-expanded={Boolean(revealedSubscriptions[selectedChannelId])}
                  >
                    {revealedSubscriptions[selectedChannelId] ? "Hide subscription link" : "Reveal subscription link"}
                  </button>
                  {revealedSubscriptions[selectedChannelId] && (
                    <div className="studio-notification-qr-wrap">
                      {qrData && qrData !== "QR_ERROR" ? (
                        <img className="studio-notification-qr" src={qrData} alt="ntfy invite QR code" />
                      ) : null}
                      <a
                        href={inviteUri}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Open secure subscription link for ${channel?.display_name || label(channel?.id, "channel")}`}
                      >
                        Open secure manual link
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </section>
  );
}
